import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import {
  getValidAccessToken,
  getMe,
  hasStoredSession,
  getLastRefreshFailureKind,
  clearStoredTokens,
  hasPersistentNativeSession,
} from '@/services/authService';

export interface User {
  id: string;
  name?: string;
  phone_number?: string;
  authMethod: 'phone';
  source?: string;
  topic_ids?: number[];
  is_guest?: boolean;
  is_registered?: boolean;
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (userData: Partial<User>) => void;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  refreshAuthState: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const AUTH_SNAPSHOT_KEY = 'booka_auth_snapshot';

const readAuthSnapshot = (): User | null => {
  try {
    const raw = localStorage.getItem(AUTH_SNAPSHOT_KEY);
    return raw ? JSON.parse(raw) as User : null;
  } catch {
    return null;
  }
};

const writeAuthSnapshot = (user: User | null) => {
  try {
    if (!user) {
      localStorage.removeItem(AUTH_SNAPSHOT_KEY);
      return;
    }
    localStorage.setItem(AUTH_SNAPSHOT_KEY, JSON.stringify(user));
  } catch {}
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

const normalizeUserProfile = (userProfile: Awaited<ReturnType<typeof getMe>>): User => ({
  id: userProfile.username || 'user',
  name: userProfile.name || undefined,
  phone_number: userProfile.phone_number || undefined,
  authMethod: 'phone',
  is_guest: userProfile.is_guest || false,
  is_registered: userProfile.is_registered ?? true,
});

const clearLegacyGuestSession = async (): Promise<boolean> => {
  if (!hasPersistentNativeSession()) return false;

  console.warn('[AuthContext] Clearing legacy persisted guest session.');
  await clearStoredTokens();
  return true;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const bootstrapAuth = async () => {
      try {
        const token = await getValidAccessToken();
        if (!mounted) return;

        if (!token) {
          const snapshot = readAuthSnapshot();
          const storedSession = await hasStoredSession();
          const failureKind = getLastRefreshFailureKind();

          if ((failureKind === 'network' || failureKind === 'server') && storedSession && snapshot) {
            console.warn('[AuthContext] Using local auth snapshot until refresh can succeed again.');
            setUser(snapshot);
          } else {
            setUser(null);
            writeAuthSnapshot(null);
          }
          return;
        }

        setUser((prev) => prev ?? readAuthSnapshot() ?? { id: 'user', authMethod: 'phone' });

        const tryFetchProfile = async (attempt = 1): Promise<void> => {
          try {
            console.log(`[AuthContext] Fetching user profile (attempt ${attempt})...`);
            const userProfile = await getMe();
            if (!mounted) return;

            if (userProfile.is_guest || !userProfile.is_registered) {
              const cleared = await clearLegacyGuestSession();
              if (!mounted) return;

              if (cleared) {
                setUser(null);
                writeAuthSnapshot(null);
                return;
              }
            }

            const userData = normalizeUserProfile(userProfile);
            console.log('[AuthContext] User profile loaded:', userData);
            setUser(userData);
            writeAuthSnapshot(userData);
          } catch (e) {
            if (!mounted) return;

            if (attempt < 2) {
              console.log('[AuthContext] Profile fetch failed, retrying...');
              setTimeout(() => { tryFetchProfile(attempt + 1); }, 500);
            } else {
              console.warn('[AuthContext] Profile fetch failed, preserving current auth state.');
              setUser((prev) => {
                const nextUser = prev ?? readAuthSnapshot() ?? { id: 'user', authMethod: 'phone' };
                writeAuthSnapshot(nextUser);
                return nextUser;
              });
            }
          } finally {
            if (mounted) setIsLoading(false);
          }
        };

        await tryFetchProfile(1);
      } catch (error) {
        console.log('[AuthContext] No valid authentication found');
        if (mounted) {
          setUser(null);
          writeAuthSnapshot(null);
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    bootstrapAuth();
    return () => {
      mounted = false;
    };
  }, []);

  const login = useCallback((userData: Partial<User>) => {
    const newUser: User = {
      id: userData.id || 'user',
      name: userData.name,
      phone_number: userData.phone_number,
      authMethod: 'phone',
      is_guest: userData.is_guest,
      is_registered: userData.is_registered,
    };
    setUser(newUser);
    writeAuthSnapshot(newUser);
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    writeAuthSnapshot(null);
  }, []);

  const updateUser = useCallback((userData: Partial<User>) => {
    setUser((currentUser) => {
      const nextUser: User = currentUser
        ? { ...currentUser, ...userData }
        : {
            id: userData.id || 'user',
            name: userData.name,
            phone_number: userData.phone_number,
            authMethod: 'phone',
            is_guest: userData.is_guest,
            is_registered: userData.is_registered,
          };

      writeAuthSnapshot(nextUser);
      return nextUser;
    });
  }, []);

  const refreshAuthState = useCallback(async () => {
    try {
      const token = await getValidAccessToken();

      if (!token) {
        const snapshot = readAuthSnapshot();
        const storedSession = await hasStoredSession();
        const failureKind = getLastRefreshFailureKind();

        if ((failureKind === 'network' || failureKind === 'server') && storedSession && snapshot) {
          setUser(snapshot);
          return;
        }

        setUser(null);
        writeAuthSnapshot(null);
        return;
      }

      try {
        const userProfile = await getMe();
        if (userProfile.is_guest || !userProfile.is_registered) {
          const cleared = await clearLegacyGuestSession();
          if (cleared) {
            setUser(null);
            writeAuthSnapshot(null);
            return;
          }
        }

        const userData = normalizeUserProfile(userProfile);
        console.log('[AuthContext] refreshAuthState: Profile fetched from server', userData);
        setUser(userData);
        writeAuthSnapshot(userData);
      } catch (e) {
        console.warn('[AuthContext] refreshAuthState: profile fetch failed', e);
        setUser((prev) => {
          const nextUser = prev ?? readAuthSnapshot() ?? { id: 'user', authMethod: 'phone' };
          writeAuthSnapshot(nextUser);
          return nextUser;
        });
      }
    } catch (error) {
      console.log('[AuthContext] refreshAuthState: No valid authentication');
      setUser(null);
      writeAuthSnapshot(null);
    }
  }, []);

  const value = useMemo(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateUser,
    refreshAuthState,
  }), [user, isLoading, login, logout, updateUser, refreshAuthState]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
